import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  HomeIcon,
  CalendarDaysIcon,
  UsersIcon,
  Cog6ToothIcon,
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  MapPinIcon,
  PlusCircleIcon,
  AdjustmentsHorizontalIcon,
  ReceiptPercentIcon,
  BanknotesIcon,
  GiftIcon,
  ClipboardDocumentListIcon,
  EnvelopeIcon,
  ArrowsRightLeftIcon
} from '@heroicons/react/24/outline';
import './AdminLayout.css';

const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Buchhaltungsrolle (Anlage v1.4 Abschnitt 2.7 Punkt 9): nur Rechnungen und Zahlungen
  const userRole = (user as any)?.Role ?? (user as any)?.role;
  const isAccounting = userRole === 'Accounting' || userRole === 5;

  const allMenuItems = [
    { path: '/admin', label: 'Dashboard', icon: HomeIcon, exact: true },
    { path: '/admin/bookings', label: 'Buchungen', icon: CalendarDaysIcon },
    { path: '/admin/quotes', label: 'Angebote', icon: ClipboardDocumentListIcon },
    { path: '/admin/customers', label: 'Kunden', icon: UsersIcon },
    { path: '/admin/calendar', label: 'Kalender', icon: MapPinIcon },
    { path: '/admin/addons', label: 'Zusatzoptionen', icon: PlusCircleIcon },
    { path: '/admin/tour-types', label: 'Tour-Typen', icon: Cog6ToothIcon },
    { path: '/admin/tour-type-config', label: 'Tour-Konfiguration', icon: AdjustmentsHorizontalIcon },
    // T-046 (11.09.2026, Erik-Auftrag): "Zahlungen" (PaymentOverview) und "Berichte" (Reports) sind
    // Prototypen ohne echte Aktionen bzw. mit irrefuehrenden Zahlen und nicht Teil der Anlage v1.4.
    // Vor dem GoLive AUSGEBLENDET (Komponenten bleiben, Entscheidung nach GoLive: T-048).
    // "Dokumente" war nur ein Platzhalter (Coming Soon) und ist komplett entfernt.
    { path: '/admin/invoices', label: 'Rechnungen', icon: ReceiptPercentIcon },
    // Barrechnungen und Bordverkäufe (Anlage v1.4 Abschnitt 2.7 Punkt 10)
    { path: '/admin/cash-invoices', label: 'Barrechnungen', icon: BanknotesIcon },
    // Bankzahlungsabgleich, Erststufe CSV (Anlage v1.4 Abschnitt 2.5 Punkt 10)
    { path: '/admin/bank-reconciliation', label: 'Bankabgleich', icon: ArrowsRightLeftIcon },
    { path: '/admin/vouchers', label: 'Gutscheine', icon: GiftIcon },
    { path: '/admin/email-queue', label: 'E-Mails', icon: EnvelopeIcon },
    { path: '/admin/settings', label: 'Einstellungen', icon: Cog6ToothIcon }
  ];

  // Barrechnungen gehören zur Buchhaltung (Anlage v1.4 Abschnitt 2.7 Punkt 10)
  // T-046: /admin/payments ausgeblendet, Buchhaltung arbeitet mit Rechnungen, Barrechnungen, Bankabgleich
  const accountingPaths = ['/admin/invoices', '/admin/cash-invoices', '/admin/bank-reconciliation'];
  const menuItems = isAccounting
    ? allMenuItems.filter((item) => accountingPaths.includes(item.path))
    : allMenuItems;

  // Route-Guard fuer Buchhaltung: alle anderen Admin-Seiten auf Rechnungen umlenken
  useEffect(() => {
    if (isAccounting && !accountingPaths.some((p) => location.pathname.startsWith(p))) {
      navigate('/admin/invoices', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAccounting, location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleBookingCalendar = () => {
    navigate('/admin/booking-new');
  };

  return (
    <div className="app-main">
      {/* Shared Header like customer version */}
      <header className="app-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Buchungssystem</h1>
          </div>

          {/* Admin Navigation */}
          <nav className="main-navigation">
            {!isAccounting && (
              <NavLink
                to="/admin/booking-new"
                className="nav-tab"
                onClick={handleBookingCalendar}
              >
                <span className="nav-icon">📅</span>
                <span className="nav-text">Buchungskalender</span>
              </NavLink>
            )}
            <NavLink
              to={isAccounting ? '/admin/invoices' : '/admin'}
              className="nav-tab"
            >
              <span className="nav-icon">⚙️</span>
              <span className="nav-text">{isAccounting ? 'Buchhaltung' : 'Admin Panel'}</span>
            </NavLink>
          </nav>

          {/* User Menu */}
          <div className="user-menu">
            <div className="user-info">
              <span className="user-name">{user?.firstName} {user?.lastName}</span>
              <span className="admin-badge">{isAccounting ? 'Buchhaltung' : 'Admin'}</span>
            </div>
            <button className="logout-button" onClick={handleLogout}>
              <ArrowLeftOnRectangleIcon className="logout-icon" />
              <span className="logout-text">Abmelden</span>
            </button>
          </div>
        </div>
      </header>

      <div className="admin-layout">
        {/* Wappen-Logo auf Sidebar-Content-Grenze */}
        <img
          src="/logo-frankenfloss.svg"
          alt="Frankenfloss Logo"
          className="wappen-logo"
        />

        {/* Mobile Header */}
        <div className="mobile-header">
          <button
            className="menu-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
          <h1 className="mobile-title">Admin Panel</h1>
        </div>

        {/* Sidebar */}
        <aside className={`admin-sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
          <div className="sidebar-header">
            <h2>Admin Panel</h2>
            <p>Verwaltung</p>
          </div>
          <nav className="sidebar-nav">
            {menuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.exact}
                className={({ isActive }) =>
                  `nav-item ${isActive ? 'nav-item-active' : ''}`
                }
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="sidebar-icon" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="sidebar-footer">
            <button className="logout-btn" onClick={handleLogout}>
              <ArrowLeftOnRectangleIcon className="sidebar-icon" />
              <span>Abmelden</span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="admin-main">
          <Outlet />
        </main>

        {/* Mobile Overlay */}
        {sidebarOpen && (
          <div
            className="mobile-overlay"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export default AdminLayout;